import { supabase } from "@/lib/supabase";
import { CallService } from "./callService";
import type { InterviewFeedback, InterviewFeedbackInput } from "@/types/video-call/feedback";

export class FeedbackService {
  /**
   * Submit interview feedback by updating call_logs
   */
  static async submitFeedback(
    callId: string,
    organizationId: string,
    feedbackInput: InterviewFeedbackInput
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await CallService.submitInterviewFeedback(callId, organizationId, feedbackInput);
      console.log("[FeedbackService] Feedback submitted successfully via CallService for call:", callId);
      return { success: true };
    } catch (error) {
      console.error("[FeedbackService] Unexpected error:", error);

      const message =
        error instanceof Error ? error.message : "Unknown error";

      if (
        (error instanceof TypeError && error.message === "Failed to fetch") ||
        message.toLowerCase().includes("network error")
      ) {
        return {
          success: false,
          error: "Unable to submit feedback. Please check your internet connection and try again."
        };
      }

      return {
        success: false,
        error: message
      };
    }
  }

  /**
   * Get feedback for a specific call
   */
  static async getFeedback(callId: string): Promise<InterviewFeedback | null> {
    try {
      const { data, error } = await supabase
        .from("call_logs")
        .select("interview_feedback")
        .eq("id", callId)
        .single();

      if (error) {
        console.error("[FeedbackService] Error fetching feedback:", error);
        return null;
      }

      return data?.interview_feedback || null;
    } catch (error) {
      console.error("[FeedbackService] Unexpected error:", error);
      return null;
    }
  }

  /**
   * Check if feedback exists for a call
   */
  static async hasFeedback(callId: string): Promise<boolean> {
    const feedback = await this.getFeedback(callId);
    return feedback !== null;
  }
}
